/**
 * 🏛️ Four Pillars — public marketing landing (Iter332 · 2026-03-02)
 * ═══════════════════════════════════════════════════════════════════
 * Standalone route at `/four-pillars` referenced from the homepage
 * Action Grid ("Our Foundation" card). Communicates the 24-month
 * transformation model to donors, employer partners, and prospective
 * residents alike.
 *
 * Structure:
 *   1. Header + Hero — title, subtitle tag, feature banner quote.
 *   2. Four alternating pillar sections (image left/right alternating
 *      on desktop, stacked on mobile) with real Unsplash photography.
 *   3. Closing CTA rail — links back to /intake + /impact.
 *
 * IMAGE DIRECTIVE (SA): NO AI-generated stock. Use direct Unsplash
 * URLs — mapped 1:1 to the four pillars per SA-supplied brief.
 */
import { useEffect } from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import useDocumentMeta from "../hooks/useDocumentMeta";
import {
  CheckCircle2, ArrowRight, Quote, Columns3,
} from "lucide-react";
// 🩹 Iter348 · DACE Sprint 1a — pillar copy hoisted to shared module so
// the marketing site + STS Touchpoint modal + Executive Dashboard chips
// all render from ONE source of truth.
import { PILLARS } from "../lib/pillars";

// ══════════════════════════════════════════════════════════════════════
// Component
// ══════════════════════════════════════════════════════════════════════
export default function FourPillars() {
  useDocumentMeta({
    title:       "The Four Pillars of Commitment to Career | Streets to Stability | The Exchange Alliance",
    description: "Explore the four-pillar model — Inner Strength, Vocation, Christ-Centered Counseling, and Daily Touchpoint — driving our 24-month Streets to Stability Road Map.",
    keywords:    "four pillars, streets to stability, transformation road map, sts homework, inner strength, vocation, spiritual counseling, daily touchpoint, workforce reentry",
    canonical:   "https://theexchangealliance.com/four-pillars",
  });
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <main data-testid="four-pillars-root" className="bg-[#FAFAF7] text-[#0A0612] font-sans">
      <h1 className="sr-only">The Four Pillars of Commitment to Career · Streets to Stability</h1>
      <Header />

      {/* ─── HERO ─────────────────────────────────────────────────── */}
      <section
        className="relative overflow-hidden bg-[#0A0612] text-[#F5F1E8] py-20 md:py-28"
        data-testid="four-pillars-hero"
      >
        {/* Photographic background — architectural pillars, subtly tinted */}
        <div
          aria-hidden
          className="absolute inset-0 bg-cover bg-center opacity-40"
          style={{
            backgroundImage:
              "url('https://images.unsplash.com/photo-1519681393784-d120267933ba?auto=format&fit=crop&q=80&w=2000')",
          }}
        />
        <div
          aria-hidden
          className="absolute inset-0 bg-gradient-to-b from-[#0A0612]/70 via-[#0A0612]/60 to-[#0A0612]"
        />

        <div className="relative mx-auto max-w-[1200px] px-6 md:px-12 lg:px-24">
          <div className="font-mono text-[10px] md:text-xs tracking-[0.35em] uppercase text-[#C9A227] mb-4">
            Streets to Stability
          </div>
          <h2
            className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight max-w-4xl leading-[1.05]"
            data-testid="four-pillars-title"
          >
            Serving the Last, the Least and the Lost:{" "}
            {/* "HIS" — underlined in gold to anchor the divine pronoun.
                PATCH-498: headline is now the brand tagline. */}
            <span
              className="text-[#F4D88A] underline decoration-[#C9A227] decoration-4 underline-offset-8"
              data-testid="four-pillars-title-his"
            >
              HIS
            </span>{" "}
            <span className="text-[#F4D88A]">Mission,</span>{" "}
            <span className="text-[#F4D88A] underline decoration-[#C9A227] decoration-4 underline-offset-8">HIS</span>{" "}
            <span className="text-[#F4D88A]">Mandate.</span>
          </h2>
          {/* Secondary display line — three-tier headline per SA 2026-03-02.
              Main line above delivers the human mandate; the two lines
              below tier the program name in progressively lighter weights
              so "The Four Pillars" reads as the framework and
              "Commitment to Career" as the destination. */}
          <div className="mt-5 space-y-1 max-w-4xl">
            <div
              className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#F4D88A] leading-tight"
              data-testid="four-pillars-title-secondary"
            >
              The Four Pillars
            </div>
            <div className="font-display text-xl sm:text-2xl lg:text-3xl font-medium tracking-tight text-[#E8E5DA] leading-tight">
              Commitment to Career
            </div>
          </div>
          <p className="mt-6 text-base md:text-lg text-[#E8E5DA] max-w-3xl leading-relaxed">
            A 24-month, whole-person transformation built on an unbreakable foundation.
          </p>

          {/* Featured quote banner */}
          <div
            className="mt-10 max-w-3xl bg-[#F5F1E8]/[0.06] backdrop-blur-sm border border-[#C9A227]/40 p-6 md:p-8"
            data-testid="four-pillars-quote-banner"
          >
            <Quote className="h-6 w-6 text-[#C9A227] mb-4" aria-hidden />
            <p className="text-[#F5F1E8] text-base md:text-lg leading-relaxed italic">
              At Streets to Stability, true independence is not an event —
              it is a <strong className="not-italic text-[#F4D88A]">24-month process</strong> built
              on an unbreakable foundation. Our Four-Pillar Model addresses the
              whole person: anchoring the heart in Christ, rebuilding
              emotional identity, mastering professional and practical life
              skills, and maintaining daily accountability.
            </p>
          </div>

          {/* Pillar navigation chips */}
          <div className="mt-10 flex flex-wrap items-center gap-3">
            {PILLARS.map((p) => (
              <a
                key={p.n}
                href={`#pillar-${p.n}`}
                data-testid={`four-pillars-nav-${p.n}`}
                className="inline-flex items-center gap-2 px-3 py-2 border border-[#C9A227]/40 hover:border-[#C9A227] bg-[#F5F1E8]/[0.04] hover:bg-[#F5F1E8]/[0.08] transition-colors font-mono text-[10px] tracking-[0.22em] uppercase text-[#F5F1E8]"
              >
                <span className="text-[#C9A227] font-bold">{p.n.toString().padStart(2, "0")}</span>
                <span className="hidden sm:inline">{p.title.split(" & ")[0]}</span>
                <span className="sm:hidden">Pillar {p.n}</span>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* ─── PILLARS ──────────────────────────────────────────────── */}
      <section className="py-16 md:py-24">
        <div className="mx-auto max-w-[1200px] px-6 md:px-12 lg:px-24 space-y-16 md:space-y-24">
          {PILLARS.map((p, idx) => {
            const IconEl = p.icon;
            const reverse = idx % 2 === 1; // alternate photo side
            return (
              <article
                key={p.n}
                id={`pillar-${p.n}`}
                data-testid={`pillar-${p.n}`}
                className="grid grid-cols-1 lg:grid-cols-12 gap-8 md:gap-12 items-center scroll-mt-20"
              >
                {/* Image */}
                <div
                  className={`lg:col-span-6 ${reverse ? "lg:order-2" : ""}`}
                >
                  {/* 🖼 PATCH-266 · When the pillar photo is a
                      portrait (e.g. Pillar 01), swap to `object-contain`
                      with a soft neutral backdrop and tighter max-height
                      so the subject reads at a comfortable size instead
                      of over-cropping. Landscape stock photos still use
                      `object-cover` full-bleed. */}
                  <div
                    className={`relative overflow-hidden rounded-2xl shadow-md group ${p.photoFit === "contain" ? "bg-[#0A0612]" : ""}`}
                  >
                    <img
                      src={p.photo}
                      alt={p.photoAlt}
                      loading="lazy"
                      className={
                        p.photoFit === "contain"
                          ? "w-full h-[300px] md:h-[380px] object-contain transition-transform duration-500 group-hover:scale-[1.02] p-3 md:p-4"
                          : "w-full h-[300px] md:h-[420px] object-cover transition-transform duration-500 group-hover:scale-105"
                      }
                      data-testid={`pillar-${p.n}-photo`}
                    />
                    <div
                      aria-hidden
                      className={
                        p.photoFit === "contain"
                          ? "pointer-events-none absolute inset-0 bg-gradient-to-tr from-[#0A0612]/25 via-transparent to-transparent"
                          : "absolute inset-0 bg-gradient-to-tr from-[#0A0612]/50 via-transparent to-transparent"
                      }
                    />
                    {/* Pillar number over-badge */}
                    <div
                      className="absolute top-4 left-4 bg-[#0A0612]/85 backdrop-blur text-[#F4D88A] px-3 py-1.5 font-mono text-[10px] tracking-[0.22em] uppercase font-bold border border-[#C9A227]/40"
                    >
                      Pillar {p.n.toString().padStart(2, "0")}
                    </div>
                  </div>
                </div>

                {/* Text */}
                <div className={`lg:col-span-6 ${reverse ? "lg:order-1" : ""}`}>
                  {/* Kicker */}
                  <div className="font-mono text-[10px] md:text-xs tracking-[0.32em] uppercase text-[#C9A227] font-bold mb-3">
                    {p.kicker}
                  </div>

                  {/* Title with icon badge */}
                  <div className="flex items-start gap-4 mb-4">
                    <div
                      className="h-12 w-12 md:h-14 md:w-14 rounded-full grid place-items-center shrink-0 border-2"
                      style={{ background: p.accent, borderColor: p.accent, color: "#F4D88A" }}
                      aria-hidden
                    >
                      <IconEl className="h-5 w-5 md:h-6 md:w-6" />
                    </div>
                    <h3 className="font-display text-2xl md:text-3xl lg:text-4xl font-bold tracking-tight text-[#0A0612] leading-tight">
                      {p.title}
                    </h3>
                  </div>

                  {/* Subtitle quote */}
                  <p className="text-[#0A0612]/70 text-base md:text-lg italic leading-relaxed mb-6 pl-4 border-l-2 border-[#C9A227]">
                    {p.subtitle}
                  </p>

                  {/* Bullets */}
                  <ul className="space-y-3">
                    {p.bullets.map((b, i) => (
                      <li
                        key={i}
                        className="flex items-start gap-3 text-[#0A0612]/85 text-sm md:text-base leading-relaxed"
                        data-testid={`pillar-${p.n}-bullet-${i}`}
                      >
                        <CheckCircle2
                          className="h-5 w-5 shrink-0 mt-0.5"
                          style={{ color: p.accent }}
                          aria-hidden
                        />
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </article>
            );
          })}
        </div>
      </section>

      {/* ─── CLOSING CTA ──────────────────────────────────────────── */}
      <section
        className="bg-[#0A0612] text-[#F5F1E8] py-16 md:py-20"
        data-testid="four-pillars-closing-cta"
      >
        <div className="mx-auto max-w-[1200px] px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-7">
              <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#C9A227] mb-3">
                Ready to walk the roadmap?
              </div>
              <h3 className="font-display text-3xl md:text-4xl font-bold tracking-tight text-[#F5F1E8] leading-tight">
                Two doors. One foundation.
              </h3>
              <p className="mt-4 text-[#E8E5DA] text-base md:text-lg leading-relaxed max-w-2xl">
                Whether you&apos;re looking for a seat at the table, want to explore our
                measured community impact, or need to partner with vetted, program-supported
                workers — the Four Pillars carry every next step.
              </p>
            </div>
            <div className="lg:col-span-5 flex flex-col gap-3">
              <a
                href="/intake"
                data-testid="four-pillars-cta-intake"
                className="group flex items-center justify-between px-5 py-4 border border-[#C9A227]/60 hover:border-[#C9A227] hover:bg-[#C9A227]/10 transition-colors"
              >
                <div>
                  <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-[#C9A227] font-bold">
                    Need Support?
                  </div>
                  <div className="font-display text-lg md:text-xl font-bold text-[#F5F1E8] mt-1">
                    Ask for a seat at the table
                  </div>
                </div>
                <ArrowRight className="h-5 w-5 text-[#C9A227] group-hover:translate-x-1 transition-transform" />
              </a>
              <a
                href="/impact"
                data-testid="four-pillars-cta-impact"
                className="group flex items-center justify-between px-5 py-4 border border-[#F5F1E8]/30 hover:border-[#F5F1E8]/60 hover:bg-[#F5F1E8]/[0.06] transition-colors"
              >
                <div>
                  <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-[#E8E5DA] font-bold">
                    Measure the Impact
                  </div>
                  <div className="font-display text-lg md:text-xl font-bold text-[#F5F1E8] mt-1">
                    See the community metrics
                  </div>
                </div>
                <ArrowRight className="h-5 w-5 text-[#F5F1E8] group-hover:translate-x-1 transition-transform" />
              </a>
              <a
                href="/labor"
                data-testid="four-pillars-cta-labor"
                className="group flex items-center justify-between px-5 py-4 border border-[#F5F1E8]/30 hover:border-[#F5F1E8]/60 hover:bg-[#F5F1E8]/[0.06] transition-colors"
              >
                <div>
                  <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-[#E8E5DA] font-bold">
                    For Employers
                  </div>
                  <div className="font-display text-lg md:text-xl font-bold text-[#F5F1E8] mt-1">
                    Estimate your labor cost
                  </div>
                </div>
                <ArrowRight className="h-5 w-5 text-[#F5F1E8] group-hover:translate-x-1 transition-transform" />
              </a>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}

// Suppresses the "Columns3 imported but unused" lint warning — we export
// the icon for the homepage card that imports from this module.
export const FourPillarsIcon = Columns3;
